/**
 * Sankey layout glue for the site data-flow transform.
 *
 * Turns the aggregation-stage outputs (collapsed links, node index,
 * page consent modes) into the `SiteDataFlowSankeyNode[]` array the
 * d3-sankey renderer reads. The page-label formatter is here too — a
 * reviewer-facing presentation concern that doesn't belong in the
 * aggregation pass.
 */

import type { components } from '@trackercop/contracts';
import type { NodeIndex } from './site-data-flow-aggregation';

type DataFlowSummary = components['schemas']['DataFlowSummary'];
type DataFlowVendor = components['schemas']['DataFlowVendor'];
type DataFlowPage = components['schemas']['DataFlowPage'];
type ConsentMode = components['schemas']['ConsentMode'];

export interface SiteDataFlowSankeyNode {
  id: string;
  kind: 'page' | 'vendor' | 'recipient' | 'more-hops' | 'other-sources';
  /** Column index hint for d3-sankey. 0 = page, 1+ = vendor depth, last = recipient. */
  column: number;
  label: string;
  /**
   * For kind='vendor' / 'recipient', the underlying vendor record from
   * the API. The sankey component opens the vendor card from this.
   */
  vendor?: DataFlowVendor | null;
  /** For kind='page', the underlying API record. */
  page?: DataFlowPage;
  /** For kind='more-hops', the count of folded vendor hops. */
  hopsCollapsed?: number;
}

/** Practical upper bound for d3-sankey to read cleanly at 960px wide. */
export const SITE_DATA_FLOW_MAX_EDGES = 200;

/**
 * Sankey page label. Prefers a compact form of the URL (path + query)
 * so reviewers see "/landing/genesis?Division_D" instead of an opaque
 * pageId hash. Falls back to "page <8-char-id>" when the API didn't
 * carry a URL — happens for pre-Phase-2c rows or when the page errored
 * before `<title>` capture. Long URLs are truncated to keep the Sankey
 * column readable; the vendor card shows the full URL on hover.
 */
const PAGE_LABEL_MAX_LEN = 48;

const CONSENT_MODE_LABELS: Record<ConsentMode, string> = {
  'accept-all': 'Accept all',
  'reject-all': 'Reject all',
  'no-consent': 'No consent',
};

/**
 * The orchestrator enqueues each URL once per consent mode, so each
 * page node in the graph maps to exactly one mode in practice. We
 * derive that mode from the page's outbound edges and append it to
 * the label so reviewers can tell three same-URL rows apart.
 */
function consentModeSuffix(modes: Set<ConsentMode> | undefined): string {
  if (!modes || modes.size === 0) return '';
  if (modes.size === 1) {
    const only = modes.values().next().value as ConsentMode;
    return ` (${CONSENT_MODE_LABELS[only]})`;
  }
  return ' (mixed)';
}

export function prettyPageLabel(page: {
  pageId: string;
  url?: string | null;
  title?: string | null;
}): string {
  const url = (page.url ?? '').trim();
  if (url) {
    let pretty = url;
    try {
      const parsed = new URL(url);
      pretty = (parsed.pathname || '/') + (parsed.search ?? '');
    } catch {
      // Non-URL value (e.g. `harness://...`) — fall through to raw.
    }
    if (pretty.length > PAGE_LABEL_MAX_LEN) {
      pretty = pretty.slice(0, PAGE_LABEL_MAX_LEN - 1) + '…';
    }
    return pretty;
  }
  return `page ${page.pageId.slice(0, 8)}`;
}

export function buildSankeyNodes(
  summary: DataFlowSummary,
  index: NodeIndex,
  referencedIds: Set<string>,
  otherSourcesNode: { id: string; label: string; column: 1 } | null,
  pageConsentModes: Map<string, Set<ConsentMode>>,
): SiteDataFlowSankeyNode[] {
  const out: SiteDataFlowSankeyNode[] = [];
  for (const p of summary.pages) {
    if (!referencedIds.has(p.nodeId)) continue;
    const modes = pageConsentModes.get(p.nodeId);
    out.push({
      id: p.nodeId,
      kind: 'page',
      column: index.columnOf.get(p.nodeId) ?? 0,
      label: prettyPageLabel(p) + consentModeSuffix(modes),
      page: p,
    });
  }
  for (const v of summary.vendors) {
    if (!referencedIds.has(v.nodeId)) continue;
    if (v.kind === 'more-hops') {
      out.push({
        id: v.nodeId,
        kind: 'more-hops',
        column: index.columnOf.get(v.nodeId) ?? 1,
        label: `+${v.hopsCollapsed ?? 0} more hops`,
        hopsCollapsed: v.hopsCollapsed ?? 0,
      });
    } else {
      out.push({
        id: v.nodeId,
        kind: 'vendor',
        column: index.columnOf.get(v.nodeId) ?? 1,
        label: v.vendor?.displayName ?? 'unattributed',
        vendor: v.vendor ?? null,
      });
    }
  }
  for (const r of summary.recipients) {
    if (!referencedIds.has(r.nodeId)) continue;
    out.push({
      id: r.nodeId,
      kind: 'recipient',
      column: index.columnOf.get(r.nodeId) ?? 2,
      label: r.vendor.displayName,
      vendor: r.vendor,
    });
  }
  if (otherSourcesNode) {
    out.push({
      id: otherSourcesNode.id,
      kind: 'other-sources',
      column: otherSourcesNode.column,
      label: otherSourcesNode.label,
    });
  }
  return out;
}
